import React from "react";
import { css } from "@linaria/core";
import Modal from "@ui/ModalV2";
import Button from "@ui/Button";
import ReactSelect from "@ui/ReactSelect";
import Table from "@ui/Table";
import Text from "@ui/Text";
import { color, fonts } from "@ui";
import { spacings } from "@ui/Theme";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import t from "../../../../../i18n";
import useToasts from "@ui/Toast/useToasts";
import { arraysEqual } from "diagnostics-lib/comparator";
import { useFailureGroups, useFailureGroupsMutations } from "../../../../../api/useFailureGroups";

interface GroupOption {
  value: string;
  label: string;
}

interface P {
  projectId: string;
  isOpen: boolean;
  onRequestClose: () => void;
  enabledFailureDisplayNames: string[];
}

const FailureGroupsModal = ({
  projectId,
  isOpen,
  onRequestClose,
  enabledFailureDisplayNames,
}: P) => {
  const failureGroups = useFailureGroups(projectId);
  const { addToast } = useToasts();
  const { createFailureGroup, updateFailureGroup, deleteFailureGroup, revalidateFailureGroups } =
    useFailureGroupsMutations(projectId);
  const [saving, setSaving] = React.useState(false);
  const [customGroupNames, setCustomGroupNames] = React.useState<string[]>([]);
  const [groupSelectionByDisplayName, setGroupSelectionByDisplayName] = React.useState<
    Record<string, string | null>
  >({});
  const [searchTerm, setSearchTerm] = React.useState("");

  const sortedDisplayNames = React.useMemo(
    () => Array.from(new Set(enabledFailureDisplayNames)).sort((a, b) => a.localeCompare(b)),
    [enabledFailureDisplayNames],
  );

  const groupByDisplayName = React.useMemo(() => {
    const mapping = new Map<string, string>();
    for (const group of failureGroups) {
      for (const item of group.items) {
        mapping.set(item.failureDisplayName, group.name);
      }
    }
    return mapping;
  }, [failureGroups]);

  const groupOptions = React.useMemo(() => {
    const names = [...failureGroups.map(group => group.name), ...customGroupNames];
    return Array.from(new Set(names))
      .sort((a, b) => a.localeCompare(b))
      .map(name => ({ value: name, label: name }));
  }, [customGroupNames, failureGroups]);

  const baseSelection = React.useMemo(
    () =>
      sortedDisplayNames.reduce<Record<string, string | null>>((acc, displayName) => {
        acc[displayName] = groupByDisplayName.get(displayName) ?? null;
        return acc;
      }, {}),
    [groupByDisplayName, sortedDisplayNames],
  );

  const wasOpenRef = React.useRef(false);

  React.useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setGroupSelectionByDisplayName(baseSelection);
      setCustomGroupNames([]);
      setSearchTerm("");
    }
    wasOpenRef.current = isOpen;
  }, [baseSelection, isOpen]);

  const filteredDisplayNames = React.useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return sortedDisplayNames;
    return sortedDisplayNames.filter(displayName =>
      displayName.toLowerCase().includes(normalizedSearch),
    );
  }, [searchTerm, sortedDisplayNames]);

  const handleCreateGroup = React.useCallback((name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setCustomGroupNames(prev => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
  }, []);

  const handleGroupChange = React.useCallback(
    (displayName: string) => (option: GroupOption | null) => {
      setGroupSelectionByDisplayName(prev => ({
        ...prev,
        [displayName]: option?.value ?? null,
      }));
    },
    [],
  );

  const getGroupDisplayNames = React.useCallback(
    (groupName: string, selection: Record<string, string | null>) =>
      sortedDisplayNames.filter(displayName => selection[displayName] === groupName),
    [sortedDisplayNames],
  );

  const handleEditGroup = React.useCallback(
    async (option: GroupOption, label: string) => {
      const trimmed = label.trim();
      if (!trimmed || trimmed === option.value) return;
      if (groupOptions.some(group => group.value === trimmed)) {
        addToast("error", t("errors.conflict"));
        return;
      }

      const existingGroup = failureGroups.find(group => group.name === option.value);
      if (!existingGroup) {
        setCustomGroupNames(prev =>
          prev.map(name => (name === option.value ? trimmed : name)).filter(Boolean),
        );
        setGroupSelectionByDisplayName(prev => {
          const next = { ...prev };
          for (const displayName of Object.keys(next)) {
            if (next[displayName] === option.value) next[displayName] = trimmed;
          }
          return next;
        });
        return;
      }

      setSaving(true);
      try {
        const failureDisplayNames = getGroupDisplayNames(option.value, groupSelectionByDisplayName);
        await updateFailureGroup({
          routeParams: { projectId, groupId: existingGroup.id },
          body: {
            name: trimmed,
            failureDisplayNames,
          },
        });
        await revalidateFailureGroups();
        setGroupSelectionByDisplayName(prev => {
          const next = { ...prev };
          for (const displayName of Object.keys(next)) {
            if (next[displayName] === option.value) next[displayName] = trimmed;
          }
          return next;
        });
      } catch (error) {
        addToast("error", t("errors.backend_error"));
      } finally {
        setSaving(false);
      }
    },
    [
      addToast,
      failureGroups,
      getGroupDisplayNames,
      groupOptions,
      groupSelectionByDisplayName,
      projectId,
      revalidateFailureGroups,
      updateFailureGroup,
    ],
  );

  const handleDeleteGroup = React.useCallback(
    async (option: GroupOption) => {
      const existingGroup = failureGroups.find(group => group.name === option.value);
      if (!existingGroup) {
        setCustomGroupNames(prev => prev.filter(name => name !== option.value));
        setGroupSelectionByDisplayName(prev => {
          const next = { ...prev };
          for (const displayName of Object.keys(next)) {
            if (next[displayName] === option.value) next[displayName] = null;
          }
          return next;
        });
        return;
      }

      setSaving(true);
      try {
        await deleteFailureGroup({
          routeParams: { projectId, groupId: existingGroup.id },
        });
        await revalidateFailureGroups();
        setGroupSelectionByDisplayName(prev => {
          const next = { ...prev };
          for (const displayName of Object.keys(next)) {
            if (next[displayName] === option.value) next[displayName] = null;
          }
          return next;
        });
      } catch (error) {
        addToast("error", t("errors.backend_error"));
      } finally {
        setSaving(false);
      }
    },
    [addToast, deleteFailureGroup, failureGroups, projectId, revalidateFailureGroups],
  );

  const hasChanges = React.useMemo(
    () =>
      sortedDisplayNames.some(
        displayName =>
          (groupSelectionByDisplayName[displayName] ?? null) !== baseSelection[displayName],
      ),
    [baseSelection, groupSelectionByDisplayName, sortedDisplayNames],
  );

  const handleSave = React.useCallback(async () => {
    if (saving) return;
    if (!hasChanges) {
      onRequestClose();
      return;
    }

    setSaving(true);
    try {
      const displayNamesByGroup = new Map<string, string[]>();
      for (const displayName of sortedDisplayNames) {
        const groupName = groupSelectionByDisplayName[displayName]?.trim();
        if (!groupName) continue;
        const list = displayNamesByGroup.get(groupName) ?? [];
        list.push(displayName);
        displayNamesByGroup.set(groupName, list);
      }

      for (const [groupName, displayNames] of displayNamesByGroup) {
        displayNamesByGroup.set(
          groupName,
          displayNames.sort((a, b) => a.localeCompare(b)),
        );
      }

      const existingGroupsByName = new Map(failureGroups.map(group => [group.name, group]));
      const operations: (() => Promise<unknown>)[] = [];

      for (const group of failureGroups) {
        const desiredDisplayNames = displayNamesByGroup.get(group.name) ?? [];
        const currentDisplayNames = group.items.map(item => item.failureDisplayName);

        if (desiredDisplayNames.length === 0) {
          operations.push(() =>
            deleteFailureGroup({
              routeParams: { projectId, groupId: group.id },
            }),
          );
          continue;
        }

        if (!arraysEqual(currentDisplayNames, desiredDisplayNames, true)) {
          operations.push(() =>
            updateFailureGroup({
              routeParams: { projectId, groupId: group.id },
              body: {
                name: group.name,
                failureDisplayNames: desiredDisplayNames,
              },
            }),
          );
        }
      }

      for (const [groupName, displayNames] of displayNamesByGroup) {
        if (existingGroupsByName.has(groupName)) continue;
        if (displayNames.length === 0) continue;
        operations.push(() =>
          createFailureGroup({
            routeParams: { projectId },
            body: {
              name: groupName,
              failureDisplayNames: displayNames,
            },
          }),
        );
      }

      for (const operation of operations) {
        await operation();
      }
      await revalidateFailureGroups();
      addToast("success", t("diagnosis.legend.group_faults.toast.saved"));
      onRequestClose();
    } catch (error) {
      addToast("error", t("errors.backend_error"));
    } finally {
      setSaving(false);
    }
  }, [
    addToast,
    createFailureGroup,
    deleteFailureGroup,
    failureGroups,
    groupSelectionByDisplayName,
    hasChanges,
    onRequestClose,
    projectId,
    revalidateFailureGroups,
    saving,
    sortedDisplayNames,
    updateFailureGroup,
  ]);

  const tableData = React.useMemo(
    () => filteredDisplayNames.map(displayName => ({ displayName })),
    [filteredDisplayNames],
  );

  const tableMetadata = React.useMemo(
    () => ({
      failure: {
        label: (
          <Text type="body2" className={failureGroupsColumnHeader}>
            {t("diagnosis.legend.group_faults.column.failure")}
          </Text>
        ),
        render: (data: { displayName: string }) => (
          <span className={failureGroupsFailureName} title={data.displayName}>
            {data.displayName}
          </span>
        ),
      },
      group: {
        label: (
          <Text type="body2" className={failureGroupsColumnHeader}>
            {t("diagnosis.legend.group_faults.column.group")}
          </Text>
        ),
        render: (data: { displayName: string }) => (
          <ReactSelect<GroupOption, false>
            allowNew
            isClearable
            onCreate={handleCreateGroup}
            onEdit={handleEditGroup}
            onDelete={handleDeleteGroup}
            onChange={handleGroupChange(data.displayName)}
            options={groupOptions}
            menuPortalTarget={window.document.body}
            value={
              groupOptions.find(
                option => option.value === groupSelectionByDisplayName[data.displayName],
              ) ?? null
            }
            placeholder={t("diagnosis.legend.group_faults.select_placeholder")}
          />
        ),
      },
    }),
    [
      groupOptions,
      groupSelectionByDisplayName,
      handleCreateGroup,
      handleDeleteGroup,
      handleEditGroup,
      handleGroupChange,
    ],
  );

  return (
    <Modal
      title={t("diagnosis.legend.group_faults.modal.title")}
      subtitle={t("diagnosis.legend.group_faults.modal.subtitle")}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      size="m"
    >
      <div className={failureGroupsModal}>
        {sortedDisplayNames.length === 0 ? (
          <div className={failureGroupsEmptyState}>{t("diagnosis.legend.group_faults.empty")}</div>
        ) : (
          <>
            <SearchInputV2
              placeholder={t("diagnosis.legend.group_faults.search.placeholder")}
              className={failureGroupsSearch}
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
            />
            {filteredDisplayNames.length === 0 ? (
              <div className={failureGroupsEmptyState}>
                {t("diagnosis.legend.group_faults.search.empty")}
              </div>
            ) : (
              <div className={failureGroupsTable}>
                <Table
                  datas={tableData}
                  metadata={tableMetadata}
                  computeRowKey={(data: { displayName: string }) => data.displayName}
                />
              </div>
            )}
          </>
        )}
        <div className={failureGroupsFooter}>
          <Button variant="secondary" onClick={onRequestClose} disabled={saving}>
            {t("commons.button.cancel")}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {t("commons.button.save")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default FailureGroupsModal;

//#region CSS
const failureGroupsModal = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: ${spacings.m};
  padding: ${spacings.m} 0;
  overflow: hidden;
`;

const failureGroupsSearch = css`
  width: 100%;
  max-width: 390px;
`;

const failureGroupsTable = css`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const failureGroupsColumnHeader = css`
  color: ${color.carbon700};
  font: ${fonts.body2SemiBold};
`;

const failureGroupsFailureName = css`
  font: ${fonts.body2};
  color: ${color.carbon900};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const failureGroupsEmptyState = css`
  padding: ${spacings.m} ${spacings.xs};
  color: ${color.carbon600};
  font: ${fonts.body2};
`;

const failureGroupsFooter = css`
  display: flex;
  justify-content: flex-end;
  gap: ${spacings.s};
`;
//#endregion CSS
